import { z } from 'zod';
/**
 * Группа оффера — то, чем заявка с лендинга связывается с продуктом.
 *
 * Ведётся не у нас. Хозяин справочника — «Робот» (adrobot), мы держим
 * локальную копию и обновляем её раз в сутки. При обращении клиента в
 * Робот не ходим вовсе: номер из первого сообщения ищется только в
 * своей базе. Так задано инструкцией их разработчика 25.09.2026, и
 * причина понятная: чужой сервис, лежащий в момент входящего звонка
 * клиента, не должен останавливать разговор.
 *
 * Ключ поиска — `ktId`, номер группы в Кейтаро: именно он приходит в
 * шаблоне «Номер заявки 8841-21897». Внутренний номер Робота хранится
 * рядом для справки и для поддержки, но искать по нему нельзя: это
 * разные числа, и подмена одного другим даст «группа не найдена» там,
 * где всё на месте.
 */
/**
 * Статус группы у Робота.
 *
 * `TEST` и `ACTIVE` — на группу идёт трафик. `STOP_TRAFFIC` и
 * `NEED_UPDATE` — трафик остановлен. `DRAFT` — черновик.
 *
 * Список закрыт, и незнакомое значение — не ошибка данных, а сигнал:
 * у Робота появился новый статус, и решение по нему должен принять
 * человек. Поэтому разбор не падает, а сохраняет как есть.
 */
export const OFFER_GROUP_STATUSES = [
    'DRAFT',
    'TEST',
    'ACTIVE',
    'STOP_TRAFFIC',
    'NEED_UPDATE',
];
export const OFFER_GROUP_STATUS_LABELS = {
    DRAFT: 'Черновик',
    TEST: 'Тест',
    ACTIVE: 'Работает',
    STOP_TRAFFIC: 'Трафик остановлен',
    NEED_UPDATE: 'Требует обновления',
};
/** Идёт ли на группу трафик. Всё, кроме двух рабочих статусов, — нет. */
export function trafficOn(status) {
    return status === 'ACTIVE' || status === 'TEST';
}
/**
 * Часы колл-центра. Любая из половин может отсутствовать, и это не
 * порча данных: у части групп в Роботе выходные не заполнены вовсе.
 */
export const callCenterHoursSchema = z.object({
    weekdays: z.object({ startHour: z.number().nullable(), stopHour: z.number().nullable() }).nullable(),
    weekends: z.object({ startHour: z.number().nullable(), stopHour: z.number().nullable() }).nullable(),
    note: z.string(),
});
export const offerGroupSchema = z.object({
    id: z.uuid(),
    /** Номер Кейтаро — по нему и только по нему идёт поиск. */
    ktId: z.number().int(),
    /** Внутренний номер Робота: для справки и разговоров с их поддержкой. */
    robotId: z.number().int().nullable(),
    name: z.string(),
    countryCode: z.string().nullable(),
    productName: z.string().nullable(),
    productFormFactor: z.string().nullable(),
    productDescription: z.string().nullable(),
    categoryName: z.string().nullable(),
    language: z.string().nullable(),
    currency: z.string().nullable(),
    callCenterHours: callCenterHoursSchema.nullable(),
    /** Текст доставки для агента — как его пишет Робот, без переделки. */
    delivery: z.string(),
    status: z.string(),
    /**
     * Была ли группа в последнем успешном обходе.
     *
     * Исчезнувшие не удаляются, а помечаются: на них ссылаются прошедшие
     * диалоги, и запись «группа 21897» в разговоре полугодовой давности
     * должна оставаться читаемой. Появится снова — отметка снимется.
     */
    isActive: z.boolean(),
    syncedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
/** Сводка последней синхронизации — для экрана и для проверки живости. */
export const offerGroupSyncSchema = z.object({
    total: z.number().int(),
    active: z.number().int(),
    trafficOn: z.number().int(),
    lastSyncAt: z.iso.datetime().nullable(),
    /** Настроен ли доступ к Роботу: без него синхронизация не запускается. */
    configured: z.boolean(),
});
//# sourceMappingURL=offer-groups.js.map